<script setup lang="ts">
import { hospitalityGroup } from "@mmh/mmh/src/config.ts"
import { computed, onMounted, onUnmounted } from "vue"
import { useRoute } from "vue-router"
import { Instagram } from "~/components/icons/instagram"
import { TikTok } from "~/components/icons/tiktok"
import { NavItem, NavLink } from "~/components/nav"
import { useConcept } from "~/composables/concept"
import { useLocation } from "~/composables/locations"
import { titleCase } from "~/lib/case"

const route = useRoute()
const { concept } = useConcept()
const { socialMedia } = concept.value || {}
const { instagram, tikTok } = socialMedia || {}
const { location, hasOneLocation, contactPath } = useLocation()

const currentYear = computed(() => new Date().getFullYear())

const careersUrl = computed(() => concept.value?.external?.careers)
const contactNavItem = computed<NavItem | null>(() => {
    if (route.name === "concept") {
        return {
            name: "Contact Us",
            to: `${hospitalityGroup.contact.website}/contact-us`
        }
    }
    
    if (location.value?.external?.api?.hubspot?.formId) {
        return {
            name: "Contact Us",
            to: contactPath()
        }
    }
    
    return null
})
const navItems = computed<NavItem[]>(() => {
    const items: NavItem[] = [
        {
            name: "Careers", 
            to: careersUrl.value || "https://mmh.co/careers"
        }
    ]

    const contact = contactNavItem.value
    if (contact) {
        items.push(contact)
    }

    return items
})

const SIGNUP_ID = "50580f8d-eb38-462d-b465-4019e8540483"

function styleSignupLabel() {
    setTimeout(() => {
        const label = document.querySelector(`label[for="email-consent-checkbox-${SIGNUP_ID}"]`) as HTMLLabelElement
        if (label) label.style.color = "black"
    }, 200)
}

onMounted(() => {
    if (!document.querySelector(`script[data-id="${SIGNUP_ID}"]`)) {
        const script = document.createElement("script")
        script.setAttribute("data-id", SIGNUP_ID)
        script.src = "https://d23vrjykee3j4x.cloudfront.net/scripts/signup.js"
        document.body.appendChild(script)
    }

    document.addEventListener("click", styleSignupLabel)
})

onUnmounted(() => {
    document.removeEventListener("click", styleSignupLabel)
})
</script>

<template>
    <footer
        class="bg-secondary max-w-full text-sm flex flex-col items-center px-4 py-16"
        aria-label="Site Footer"
    >
        <div class="w-full max-w-6xl flex flex-col gap-8">
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <!-- Mobile logo: visible only below lg, receives focus first on mobile -->
                <a 
                    class="flex flex-col items-center mb-12 lg:hidden rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                    :aria-label="`Go to ${hospitalityGroup.name} home page`"
                    :href="hospitalityGroup.contact.website"
                    target="_blank"
                    referrerPolicy="no-referrer"
                    tabindex="0"
                >
                    <img
                        :src="concept?.images?.footerLogo"
                        :alt="`${titleCase(concept?.name)} Family of Restaurants Logo`"
                        class="h-32 w-auto mb-4"
                        width="256"
                        height="128"
                    >
                    <p class="text-center text-footer-foreground">
                        {{ concept?.footerTagline }}
                    </p>
                </a>
                <nav 
                    class="flex flex-col items-center gap-4" 
                    aria-label="Restaurant Concepts"
                >
                    <h2 class="uppercase text-lg tracking-widest text-footer-foreground">
                        Restaurants
                    </h2>
                    <ul class="space-y-2 text-center">
                        <li 
                            v-for="groupConcept in hospitalityGroup.concepts" 
                            :key="groupConcept.name"
                        >
                            <NavLink
                                :item="{ name: titleCase(groupConcept.name), to: groupConcept.website }"
                                class="capitalize text-xs text-footer-foreground"
                            />
                        </li>
                    </ul>
                </nav>
                <!-- Desktop logo: visible only at lg+, sits between Restaurants and Connect -->
                <a 
                    class="hidden lg:flex flex-col items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                    :aria-label="`Go to ${hospitalityGroup.name} home page`"
                    :href="hospitalityGroup.contact.website"
                    target="_blank"
                    referrerPolicy="no-referrer"
                    tabindex="0"
                >
                    <img
                        :src="concept?.images?.footerLogo"
                        :alt="`${titleCase(concept?.name)} Family of Restaurants Logo`"
                        class="h-32 w-auto mb-4"
                        width="256"
                        height="128"
                    >
                    <p class="text-center text-footer-foreground">
                        {{ concept?.footerTagline }}
                    </p>
                </a>
                <nav 
                    class="flex flex-col items-center gap-4 pt-4 lg:pt-0" 
                    aria-label="Company Links"
                >
                    <h2 class="uppercase text-lg tracking-widest text-footer-foreground">
                        Connect
                    </h2>
                    <ul class="space-y-2 text-center">
                        <li
                            v-for="item of navItems"
                            :key="item.name"
                        >
                            <NavLink
                                :item="item"
                                class="text-xs capitalize text-footer-foreground"
                            />
                        </li>
                        <li
                            v-if="location?.path && !location.closed"
                        >
                            <NavLink
                                :item="{ name: 'Hours & Location', to: `${hasOneLocation ? '' : `/${location.path}`}/hours-location` }"
                                class="text-xs capitalize text-footer-foreground"
                            />
                        </li>
                    </ul>
                    <button
                        id="loy-email-signup-button-50580f8d-eb38-462d-b465-4019e8540483"
                        aria-label="Join our mailing list"
                        class="text-footer-foreground bg-footer-button-background px-4 py-3 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                        tabindex="0"
                    >
                        Join our mailing list
                    </button>
                </nav>
            </div>
            <nav aria-label="Legal Information">
                <ul class="flex flex-wrap justify-center text-center gap-x-8 gap-y-6 w-full">
                    <li
                        v-for="item of [
                            { name: 'Terms of Use', to: '/terms-of-use' },
                            { name: 'Privacy Policy', to: '/privacy-policy' },
                            { name: 'Accessibility Statement', to: '/accessibility-statement' }
                        ]"
                        :key="item.to"
                    >
                        <RouterLink
                            :to="item.to"
                            tabindex="0"
                            class="text-xs capitalize text-footer-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background rounded-sm"
                        >
                            {{ item.name }}
                        </RouterLink>
                    </li>
                </ul>
            </nav>
            <div class="flex items-center justify-center gap-8">
                <a
                    :href="instagram.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow us on Instagram"
                    tabindex="0"
                    class="size-9 rounded-full inline-flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                >
                    <Instagram class="size-4 fill-footer-foreground" />
                </a>
                <a
                    v-if="tikTok.handle"
                    :href="tikTok.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow us on TikTok"
                    tabindex="0"
                    class="size-9 rounded-full inline-flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                >
                    <TikTok class="size-4 fill-footer-foreground" />
                </a>
            </div>
            <p class="text-center text-xs tracking-wider text-footer-foreground">
                © {{ currentYear }} {{ titleCase(concept?.parentCompany?.dba) }}.
            </p>
        </div>
    </footer>
</template>